import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { LANGUAGES } from "@/lib/constants";
import { Language } from "@/lib/types";

const languages = [
  { code: LANGUAGES.EN as Language, name: "english", flag: "🇬🇧" },
  { code: LANGUAGES.PL as Language, name: "polish", flag: "🇵🇱" },
];

export default function LanguagePicker() {
  const { i18n, t } = useTranslation();
  const [isChanging, setIsChanging] = useState(false);

  const handleLanguageChange = async (languageCode: string) => {
    setIsChanging(true);
    try {
      // Change the i18n language immediately for UI
      await i18n.changeLanguage(languageCode);

      // Store preference in localStorage
      localStorage.setItem("gpsbox-language", languageCode);
    } catch (error) {
      console.error("Failed to change language:", error);
    } finally {
      setIsChanging(false);
    }
  };

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          disabled={isChanging}
          className="w-full justify-between"
        >
          <div className="flex items-center">
            <span className="mr-2">{currentLanguage.flag}</span>
            {t(currentLanguage.name)}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => void handleLanguageChange(language.code)}
            className={i18n.language === language.code ? "bg-accent" : ""}
          >
            <span className="mr-2">{language.flag}</span>
            {t(language.name)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
